/**
 * Vendor Page - Menu Display + Add to Cart
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBrowseStore } from '@/store/browse';
import { useCartStore } from '@/store/cart';
import { MenuItem } from '@/lib/constants';
import { formatCurrency } from '@/lib/format';
import { ArrowLeft, ShoppingCart, Plus, Minus, AlertTriangle, Search, X } from 'lucide-react';
import { ProgressiveImage } from '@/components/common/ProgressiveImage';
import { WeightSelector } from '@/components/cart/WeightSelector';

export default function VendorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedVendor, menuItems, isLoadingMenu, selectVendor } = useBrowseStore();
  const { cart, addItem, clearCart } = useCartStore();

  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [showCrossVendorWarning, setShowCrossVendorWarning] = useState(false);

  useEffect(() => {
    if (id) {
      selectVendor(id);
    }
  }, [id, selectVendor]);

  // Check if we can add to cart (cross-zone protection)
  const canAddItemToCart = (_vendorId: string) => {
    if (!cart || cart.items.length === 0) return true;
    
    // Check if this vendor is in the same zone as existing cart items
    const cartZones = cart.zone_ids;
    const vendorZone = selectedVendor?.zone_id;
    
    if (cartZones.length > 0 && vendorZone && !cartZones.includes(vendorZone)) {
      return false;
    }
    
    return true;
  };

  const handleAddToCart = (item: MenuItem) => {
    // Check cross-vendor/cross-zone guard
    if (!canAddItemToCart(item.vendor_id)) {
      setSelectedItem(item);
      setShowCrossVendorWarning(true);
      return;
    }

    setSelectedItem(item);
  };

  const confirmAddToCart = async () => {
    if (!selectedItem) return;

    try {
      // Sprint 4J: addItem takes product_id and quantity
      await addItem(selectedItem.id, quantity);
      setSelectedItem(null);
      setQuantity(1);
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleClearAndAdd = () => {
    clearCart();
    setShowCrossVendorWarning(false);
    if (selectedItem) {
      addItem(selectedItem.id, quantity);
      setSelectedItem(null);
      setQuantity(1);
    }
  };

  // Group menu items by category
  const groupedItems = menuItems.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, MenuItem[]>);

  if (isLoadingMenu) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (!selectedVendor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Vendor not found</p>
          <button onClick={() => navigate('/')} className="mt-4 text-green-600 hover:text-green-700">
            ← Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold">{selectedVendor.name}</h1>
            <div className="flex items-center gap-2">
              {/* Sprint 4J: Search Button */}
              <button
                onClick={() => navigate('/search')}
                className="p-2 hover:bg-gray-100 rounded-full"
                title="Search Products"
              >
                <Search className="w-6 h-6" />
              </button>
              <button
                onClick={() => navigate('/cart')}
                className="relative p-2 hover:bg-gray-100 rounded-full"
              >
                <ShoppingCart className="w-6 h-6" />
                {cart && cart.total_items > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cart.total_items}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Vendor Info */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {selectedVendor.description && (
            <p className="text-gray-600 mb-2">{selectedVendor.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>Delivery: {formatCurrency(selectedVendor.delivery_fee)}</span>
            <span>•</span>
            <span>Min order: {formatCurrency(selectedVendor.min_order)}</span>
            <span>•</span>
            <span>{selectedVendor.delivery_eta_min}-{selectedVendor.delivery_eta_max} min</span>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {Object.keys(groupedItems).length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No menu items available
          </div>
        ) : (
          <div className="space-y-8">
            {(Object.entries(groupedItems) as [string, MenuItem[]][]).map(([category, items]) => (
              <div key={category}>
                <h2 className="text-xl font-bold mb-4">{category}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {items
                    .filter((item) => item.is_active)
                    .map((item) => (
                      <div
                        key={item.id}
                        className="bg-white border border-gray-200 rounded-lg p-4 flex gap-4"
                      >
                        {/* Product Image (Sprint 4I) */}
                        {item.primaryImage && (
                          <div className="flex-shrink-0">
                            <ProgressiveImage
                              image={item.primaryImage}
                              alt={item.name}
                              className="w-32 h-32 md:w-40 md:h-40 rounded-lg"
                              size="md"
                              lazy={true}
                            />
                          </div>
                        )}
                        
                        {/* Product Info */}
                        <div className="flex-1 flex justify-between items-center">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{item.name}</h3>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                            )}
                            <div className="mt-2 flex items-center gap-3">
                              <span className="font-bold text-green-600">
                                {formatCurrency(item.price_today)}/{item.unit}
                              </span>
                              {item.stock_status === 'out_of_stock' ? (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                  Out of stock
                                </span>
                              ) : item.stock_status === 'low_stock' ? (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                  Low stock
                                </span>
                              ) : null}
                            </div>
                          </div>
                          
                          {/* Add to Cart Button */}
                          <button
                            onClick={() => handleAddToCart(item)}
                            disabled={item.stock_status === 'out_of_stock'}
                            className="ml-4 bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add to Cart Modal */}
      {selectedItem && !showCrossVendorWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <button
              onClick={() => {
                setSelectedItem(null);
                setQuantity(1);
              }}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full z-10 bg-white shadow-md"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              {/* Product Image (Sprint 4I) */}
              {selectedItem.primaryImage && (
                <div className="mb-6">
                  <ProgressiveImage
                    image={selectedItem.primaryImage}
                    alt={selectedItem.name}
                    className="w-full h-48 md:h-64 rounded-xl shadow-lg object-cover"
                    size="lg"
                    lazy={false}
                  />
                </div>
              )}
              
              <h3 className="text-2xl font-bold mb-4">{selectedItem.name}</h3>
              {selectedItem.description && (
                <p className="text-gray-600 mb-4">{selectedItem.description}</p>
              )}
              <div className="flex items-center justify-between mb-6">
                <span className="text-xl font-bold text-green-600">
                  {formatCurrency(selectedItem.price_today)}/{selectedItem.unit}
                </span>
              </div>

              {/* Fractional Weight Selector (Sprint 4J) */}
              {selectedItem.unit === 'kg' ? (
                <div className="mb-6">
                  <WeightSelector
                    pricePerKg={Number(selectedItem.price_today)}
                    initialWeight={quantity}
                    onChange={setQuantity}
                    min={0.125}
                    max={50}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center gap-4 mb-6">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="text-2xl font-bold w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={confirmAddToCart}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg"
                >
                  Add to Cart • {formatCurrency(selectedItem.price_today * quantity)}
                </button>
                <button
                  onClick={() => {
                    setSelectedItem(null);
                    setQuantity(1);
                  }}
                  className="w-full py-3 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cross-Vendor Warning Modal */}
      {showCrossVendorWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
              <h3 className="text-xl font-bold">Clear Cart?</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Your cart contains items from another vendor. You can only order from one vendor at a time.
              Clear your cart to add items from this vendor?
            </p>
            <div className="space-y-3">
              <button
                onClick={handleClearAndAdd}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg"
              >
                Clear Cart & Add Item
              </button>
              <button
                onClick={() => {
                  setShowCrossVendorWarning(false);
                  setSelectedItem(null);
                  setQuantity(1);
                }}
                className="w-full py-3 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}








